import React, { useRef } from 'react';
import { format, isSameDay, addWeeks, subWeeks } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useTalentCalendar } from '../../contexts/TalentCalendarContext';
import './Calendar.css';
import { WeekEvent } from './EventComponent';

const TalentWeekView = ({ onEventClick, onDateSelect, events: propEvents }) => {
  const navigate = useNavigate();
  const contextValues = useTalentCalendar() || {};
  const { 
    currentDate = new Date(), 
    selectedDate = new Date(), 
    events: contextEvents = [], 
    setCurrentDate = () => {} 
  } = contextValues;
  
  // Use events from props if provided, otherwise use from context
  const events = propEvents || contextEvents || [];
  
  const scrollContainerRef = useRef(null);
  
  // Get the start and end of the current week
  const weekStart = new Date(currentDate);
  weekStart.setDate(currentDate.getDate() - currentDate.getDay() + 1); // Start from Monday
  
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  
  // Generate days array for the week - 7 days (Monday to Sunday)
  const days = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + i);
    days.push({
      date,
      isToday: isSameDay(date, new Date()),
      isSelected: isSameDay(date, selectedDate)
    });
  }
  
  // Navigation functions
  const goToPreviousWeek = () => {
    setCurrentDate(subWeeks(currentDate, 1));
  };

  const goToNextWeek = () => {
    setCurrentDate(addWeeks(currentDate, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };
  
  // Get events for a specific day
  const getEventsForDay = (day) => {
    if (!events || !Array.isArray(events) || events.length === 0) {
      return { availability: null, bookings: [], notes: [] };
    }

    // Separate availability/vacation/awaiting events from bookings
    const dayEvents = events.filter(event => {
      if (!event || !event.start_date) {
        return false;
      }

      try {
        const eventDate = new Date(event.start_date);
        const isMatch = isSameDay(day.date, eventDate);
        return isMatch;
      } catch (error) {
        console.error(`Error comparing dates for event:`, error, event);
        return false;
      }
    });

    // Find availability status for the day
    const availabilityEvent = dayEvents.find(event => 
      event.type === 'availability' || event.type === 'vacation' || event.type === 'awaiting'
    );

    // Get bookings for the day
    const bookingEvents = dayEvents.filter(event => event.type === 'booking');

    // Get notes for the day
    const noteEvents = dayEvents.filter(event => event.type === 'note');

    return {
      availability: availabilityEvent,
      bookings: bookingEvents,
      notes: noteEvents
    };
  };

  const handleDateSelect = (date) => {
    if (onDateSelect) {
      onDateSelect(date);
    }
  };

  // Get background color for availability status
  const getAvailabilityColor = (type) => {
    switch (type) {
      case 'availability':
        return '#2ACEF1';
      case 'vacation':
        return '#F12A5D';
      case 'awaiting':
        return '#FFA500';
      default:
        return 'transparent';
    }
  };

  return (
    <div className="calendar-week-view" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className="calendar-week-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="calendar-button" onClick={goToPreviousWeek}>
            Previous
          </button>
          <button className="calendar-button" onClick={goToToday}>
            Today
          </button>
          <button className="calendar-button" onClick={goToNextWeek}>
            Next
          </button>
        </div>
        <h2>{format(days[0].date, 'MMMM d')} - {format(days[6].date, 'MMMM d, yyyy')}</h2>
      </div>
      
      <div className="calendar-week-grid" style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        {/* Day headers row - 7 days */}
        <div className="calendar-week-days-header">
          {days.map((day, dayIndex) => (
            <div 
              key={`header-${dayIndex}`} 
              className={`calendar-day-header ${day.isToday ? 'calendar-day-today' : ''}`}
            >
              <div>{format(day.date, 'EEE')}</div>
              <div className="calendar-day-number">{format(day.date, 'd')}</div>
            </div>
          ))}
        </div>
        
        {/* Week content with synchronized scrolling - 7 columns */}
        <div 
          className="calendar-week-content"
          ref={scrollContainerRef}
          style={{ 
            overflowY: 'auto',
            flex: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            height: '100%',
            minHeight: 0,
            gap: 0
          }}
        >
          {days.map((day, dayIndex) => {
            const { availability, bookings, notes } = getEventsForDay(day);
            
            return (
              <div 
                key={`day-${dayIndex}`} 
                className={`calendar-day-column ${day.isToday ? 'calendar-day-today' : ''} ${isSameDay(day.date, selectedDate) ? 'calendar-day-selected' : ''}`}
                onClick={() => handleDateSelect(day.date)}
                style={{ 
                  height: 'auto',
                  borderRight: dayIndex < 6 ? '1px solid #ddd' : 'none',
                  position: 'relative'
                }}
              >
                {/* Availability status block at the top */}
                {availability && (
                  <div
                    style={{
                      backgroundColor: getAvailabilityColor(availability.type),
                      height: '40px',
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: '700',
                      color: '#fff',
                      textTransform: 'uppercase',
                      border: '2px solid #fff',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    {availability.type === 'availability' && 'AVAILABLE'}
                    {availability.type === 'vacation' && 'VACATION'}
                    {availability.type === 'awaiting' && 'AWAITING'}
                  </div>
                )}

                {/* Notes indicator */}
                {notes.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '5px',
                      backgroundColor: '#fff0f0',
                      borderBottom: '1px solid #ffd0d0',
                      fontSize: '12px',
                      color: '#d00'
                    }}
                  >
                    <span style={{ marginRight: '5px' }}>📝</span>
                    {notes.length} note{notes.length > 1 ? 's' : ''}
                  </div>
                )}

                <div className="calendar-day-content">
                  {/* Display bookings for this specific day */}
                  <div className="calendar-day-events-container">
                    {bookings.map((event, eventIndex) => (
                      <div 
                        key={`${event.id || eventIndex}-${eventIndex}`} 
                        className="calendar-day-event-wrapper calendar-week-event-compact"
                      >
                        <WeekEvent
                          event={event}
                          onEventClick={onEventClick}
                          compact={true}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TalentWeekView; 